/**
 * Create a market view from scratch — the `/workspace_market/:type` route.
 *
 * The one page whose `type` comes from the URL, because there's no market view to read it off yet:
 * here the type is the user's *choice*, made in the creation dialog, not a property of an existing
 * entity. The other two pages resolve it from the market view itself.
 */
import type { MarketViewType } from "@eu.mdotm.api/sphere";
import { useFunctionalAreas } from "$root/App/context";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { LeavePrompt } from "$root/components/LeavePrompt";
import { PageHeader } from "$root/components/PageHeader";
import { useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { Button } from "@mdotm/shadcn/mdotm/components";
import fastDeepEqual from "fast-deep-equal";
import { useTranslation } from "react-i18next";
import {
	MARKET_VIEW_LIST_ROUTE,
	MarketViewEditor,
	useMarketViewScenarios,
	useMarketViewWorkspaceConfig,
	useSaveMarketView,
} from "./shared";

const NewMarketView = ({
	type: rawType,
	isCustom: rawIsCustom,
	intentId,
}: {
	/** Which of the two market view kinds to build. */
	type?: string;
	isCustom?: string;
	intentId?: string;
}): React.JSX.Element => {
	const { t } = useTranslation();
	const { push } = useTypedNavigation();
	const crumbs = useCrumbs();

	// The router types params as plain strings, so this is the single place in the workspace where one
	// becomes a `MarketViewType` — everything downstream takes the narrowed value. The creation dialog
	// is the only entry point and always sends one of the two kinds; a hand-typed URL with anything
	// else falls through the editor's `Switch` and renders empty, exactly as it did before the split.
	const type = rawType as MarketViewType;
	const isCustom = rawIsCustom === "true";

	useFunctionalAreas(() => ({ areas: "marketView", data: {} }), []);

	const { settings, assetClassAliases } = useMarketViewWorkspaceConfig();
	const scenario = useMarketViewScenarios({ type, isCustom });
	const { pathToNotBlock, promptForNameAndSave } = useSaveMarketView({ type, isCustom, intentId, scenario });

	return (
		<>
			<PageHeader
				crumbs={[...crumbs.portfolioStudio("MarketViews"), ...crumbs.marketViewType({ marketViewType: type })]}
				titleAction={
					<div className="flex items-center space-x-2">
						<Button
							size="small"
							onClick={() => push("PortfoliosStudio", MARKET_VIEW_LIST_ROUTE)}
							palette="tertiary"
							disabled={false}
							data-qualifier="MarketViewWorkspace/Cancel"
						>
							Cancel
						</Button>
						<Button
							size="small"
							onClick={promptForNameAndSave}
							palette="primary"
							disabled={false}
							data-qualifier="MarketViewWorkspace/Save"
						>
							Save
						</Button>
					</div>
				}
			/>
			<MarketViewEditor
				type={type}
				isCustom={isCustom}
				mode="edit"
				scenario={scenario}
				settings={settings}
				assetClassAliases={assetClassAliases}
				showWidgetHeader
				lockScenarioSelection={false}
				hidePositioning={false}
				remapCommentaryToDefaults
			/>
			<LeavePrompt
				title={t("NOTIFICATION_SETTINGS.LEAVE_PROMPT.TITLE")}
				when={
					scenario.originalScenario === null
						? false
						: fastDeepEqual(scenario.originalScenario, scenario.marketScenario) === false
				}
				pathToNotBlock={pathToNotBlock}
			>
				{t("NOTIFICATION_SETTINGS.LEAVE_PROMPT.MESSAGE")}
			</LeavePrompt>
		</>
	);
};

export default NewMarketView;
